import { createContext, useContext, useEffect, useState } from 'react';
import { loginWithEmail, signupWithEmail, logoutUser } from '../services/auth';
import { auth } from '../firebase';
import { getRuntimeConfig } from '../runtimeConfig';

const AuthContext = createContext();
const API_URL = getRuntimeConfig('API_BASE_URL', '');

export function useAuth() {
    return useContext(AuthContext);
}

function buildCurrentUser(user, userData = null) {
    if (!user) {
        return null;
    }

    return {
        ...(userData || {}),
        uid: user.uid,
        email: user.email || userData?.email || '',
        emailVerified: user.emailVerified,
        displayName: user.displayName,
        photoURL: user.photoURL,
        providerId: user.providerId,
    };
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchUserData = async (user) => {
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${API_URL}/api/users/${user.uid}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Failed to fetch user data');
            return await response.json();
        } catch (error) {
            console.error('User data fetch error:', error);
            return null;
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const userData = await fetchUserData(user);
                setCurrentUser(buildCurrentUser(user, userData));
            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        defaultProtectedPath: '/lesson',
        loginWithEmail: async (email, password) => {
            const user = await loginWithEmail(email, password);
            const userData = await fetchUserData(user);
            setCurrentUser(buildCurrentUser(user, userData));
            return user;
        },
        signupWithEmail: async (email, password) => {
            const user = await signupWithEmail(email, password);
            const userData = await fetchUserData(user);
            setCurrentUser(buildCurrentUser(user, userData));
            return user;
        },
        logoutUser: async () => {
            await logoutUser();
            setCurrentUser(null);
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
